import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { formatMoneyWhole } from '@/lib/formatMoney';
import { useMyOrders, ORDER_STATUS_CONFIG, type MyOrder, type OrderStatus } from '@/hooks/useMyOrders';
import { useProductCoverImages } from '@/hooks/useProductCoverImages';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronRight, MapPin } from 'lucide-react';

function OrderStatusBadge({ status }: { status: OrderStatus }) {
  const config = ORDER_STATUS_CONFIG[status] ?? ORDER_STATUS_CONFIG.created;
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

function ProductThumb({ title, imageUrl }: { title: string; imageUrl: string | null }) {
  const letter = (title?.[0] ?? '?').toUpperCase();

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={title}
        className="h-12 w-12 rounded-2xl object-cover shrink-0"
        loading="lazy"
      />
    );
  }

  return (
    <div className="h-12 w-12 rounded-2xl bg-muted flex items-center justify-center shrink-0">
      <span className="text-sm font-medium text-muted-foreground">{letter}</span>
    </div>
  );
}

function OrderCard({ order, coverUrl }: { order: MyOrder; coverUrl: string | null }) {
  const isInactive = order.status === 'completed' || order.status === 'cancelled';

  const itemSummary = order.firstItemTitle
    ? order.itemCount > 1
      ? `${order.firstItemTitle} ×${order.firstItemQuantity} och ${order.itemCount - 1} till`
      : `${order.firstItemTitle} ×${order.firstItemQuantity}`
    : `${order.itemCount} produkter`;

  return (
    <Link to={`/app/customer/orders/${order.id}`} className="block group">
      <Card
        className={`transition-shadow ${
          isInactive ? 'opacity-70' : 'hover:shadow-[var(--card-shadow-hover)]'
        }`}
      >
        <CardContent className="flex items-center gap-3 sm:gap-4 p-4">
          <ProductThumb title={order.firstItemTitle ?? order.producerName} imageUrl={coverUrl} />

          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-sm sm:text-base truncate">
                {order.producerName}
              </p>
              <OrderStatusBadge status={order.status} />
            </div>

            <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground flex-wrap">
              <span>Beställd {format(new Date(order.createdAt), 'd MMM yyyy', { locale: sv })}</span>
              <span className="text-border">·</span>
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Hämtas hos {order.producerName}
              </span>
            </div>

            <p className="text-xs text-muted-foreground truncate">
              {itemSummary}
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span className="font-semibold text-sm sm:text-base whitespace-nowrap">
              {formatMoneyWhole(order.totalAmount, order.currency)}
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export function MyOrdersSection({ hideWhenEmpty = false }: { hideWhenEmpty?: boolean }) {
  const { data: orders, isLoading, error } = useMyOrders();

  // Batch-fetch cover images for all first products
  const productIds = (orders ?? [])
    .map((o) => o.firstProductId)
    .filter((id): id is string => !!id);
  const { data: coverMap } = useProductCoverImages(productIds, 320);

  return (
    <div>
      <h3 className="text-lg font-medium text-foreground">Beställningar</h3>

      {isLoading && (
        <div className="mt-3 space-y-3">
          {[1, 2].map((i) => (
            <Card key={i} className="border-border/60 border-dashed shadow-none">
              <CardContent className="flex items-center gap-4 p-4">
                <Skeleton className="h-12 w-12 rounded-2xl shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-3 w-52" />
                </div>
                <Skeleton className="h-5 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {error && !isLoading && (
        <Card className="mt-3 border-destructive/50 bg-destructive/5 shadow-none">
          <CardContent className="p-5 text-center text-destructive">
            <p>Kunde inte hämta beställningar. Försök igen senare.</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && (!orders || orders.length === 0) && !hideWhenEmpty && (
        <p className="mt-3 text-sm text-muted-foreground">
          Du har inga beställningar ännu.
        </p>
      )}

      {!isLoading && !error && orders && orders.length > 0 && (
        <div className="mt-3 space-y-3">
          {orders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              coverUrl={order.firstProductId ? (coverMap?.[order.firstProductId] ?? null) : null}
            />
          ))}
        </div>
      )}
    </div>
  );
}
